import { ref } from 'vue'
import { defineStore } from 'pinia'

const STORAGE_KEY = 'theme'

function applyDarkClass(isDark: boolean): void {
  document.documentElement.classList.toggle('dark', isDark)
}

export const useThemeStore = defineStore('theme', () => {
  const isDarkMode = ref(document.documentElement.classList.contains('dark'))

  function setDarkMode(value: boolean): void {
    isDarkMode.value = value
    localStorage.setItem(STORAGE_KEY, value ? 'dark' : 'light')
    applyDarkClass(value)
  }

  function toggleDarkMode(): void {
    setDarkMode(!isDarkMode.value)
  }

  return { isDarkMode, setDarkMode, toggleDarkMode }
})

/**
 * Dark-mode background mappings for components that build their background
 * class dynamically (e.g. `bg-${prop}`), where a literal `dark:` variant
 * can't be appended. Each helper returns the dark-mode class for a given
 * light background name (with or without a leading `bg-`).
 *
 * SURFACE = page/card surfaces (grey-10 is a page section → darkest canvas).
 * CONTROL = form-control fills (grey-10 is a field → one step brighter).
 */
const SURFACE_DARK_BG: Record<string, string> = {
  white: 'dark:bg-dark-surface',
  'grey-10': 'dark:bg-dark-bg',
  'grey-9': 'dark:bg-dark-surface-2',
}

const SURFACE_DARK_HOVER_BG: Record<string, string> = {
  white: 'dark:hover:bg-dark-surface',
  'grey-10': 'dark:hover:bg-dark-bg',
  'grey-9': 'dark:hover:bg-dark-surface-2',
}

const CONTROL_DARK_BG: Record<string, string> = {
  white: 'dark:bg-dark-surface',
  'grey-10': 'dark:bg-dark-surface',
  'grey-9': 'dark:bg-dark-surface-2',
}

function stripBgPrefix(bg: string): string {
  return bg.replace(/^bg-/, '')
}

export function darkSurfaceBgClass(bg: string): string {
  return SURFACE_DARK_BG[stripBgPrefix(bg)] ?? ''
}

export function darkSurfaceHoverBgClass(bg: string): string {
  return SURFACE_DARK_HOVER_BG[stripBgPrefix(bg)] ?? ''
}

export function darkControlBgClass(bg: string): string {
  return CONTROL_DARK_BG[stripBgPrefix(bg)] ?? ''
}
